/**
 * ============================================================================
 * SINGLE SOURCE OF TRUTH FOR CALENDAR AVAILABILITY
 * ============================================================================
 *
 * This module is used by BOTH booking and rescheduling calendars.
 * Any changes here affect both flows equally.
 *
 * Pre-MVP: Optimized for correctness, consistency, and safety.
 *
 * PRECEDENCE (highest to lowest):
 * P1. Closed date override → zero availability
 * P2. Custom hours date override → use override time ranges
 * P3. Blackout date (legacy) → zero availability
 * P4. Weekly hours → use day-of-week schedule
 *
 * INVARIANTS:
 * 1. Same-day cutoff in MINUTES (default 120 = 2 hours)
 * 2. Slot START time must be >= now + cutoffMinutes
 * 3. Date overrides (closed/custom) take precedence over all other checks
 * 4. Blackout dates exclude all slots for that day
 * 5. Business hours enforced at generation time
 * 6. Overbooking protection enforced in DB (not just UI)
 * 7. Public availability is STRICT: if a business has no `business_hours`
 *    row for a given day, or the row is disabled, that day is unavailable.
 *    Default 9 AM–6 PM hours are NEVER used as implicit public availability —
 *    they exist only as a suggested editable template for the dashboard
 *    (see `getSuggestedDefaultHoursTemplate`).
 */

import { supabase } from '@/integrations/supabase/client';

// ============================================================================
// TYPES
// ============================================================================

export interface BusinessHours {
  day_of_week: string; // "monday", "tuesday", etc.
  start_time: string;  // "09:00"
  end_time: string;    // "18:00"
  enabled: boolean;
}

export interface BusinessSettings {
  same_day_cutoff_minutes: number;  // Default: 120 (2 hours in minutes)
  buffer_minutes: number;            // Default: 15
  slot_interval_minutes: number;     // Default: 60
  uses_dynamic_hours: boolean;       // Default: true
}

export interface TimeSlotConfig {
  businessHours: {
    start: number; // hour (0-23)
    end: number;   // hour (0-23)
  };
  slotDuration: number; // minutes
  buffer: number;       // minutes
}

export interface BookedSlot {
  booking_time: string; // "HH:MM:SS" or "HH:MM"
  duration: number;     // minutes
}

// ============================================================================
// TIMEZONE HELPERS (private)
// ============================================================================

/**
 * Get "now" as a Date whose local getHours()/getMinutes() reflect the
 * business timezone. Uses the standard toLocaleString trick which handles DST.
 */
function getBusinessLocalNow(timezone?: string): Date {
  const now = new Date();
  if (!timezone) return now;
  try {
    return new Date(now.toLocaleString('en-US', { timeZone: timezone }));
  } catch {
    return now;
  }
}

/**
 * Get YYYY-MM-DD string for a Date in a given timezone (en-CA locale = ISO format)
 */
function getDateStringInTimezone(date: Date, timezone?: string): string {
  if (!timezone) return date.toISOString().split('T')[0];
  try {
    return date.toLocaleDateString('en-CA', { timeZone: timezone });
  } catch {
    return date.toISOString().split('T')[0];
  }
}

// ============================================================================
// DEFAULTS (fallback if DB query fails)
// ============================================================================

const DEFAULT_SETTINGS: BusinessSettings = {
  same_day_cutoff_minutes: 120,  // 2 hours in minutes
  buffer_minutes: 15,
  slot_interval_minutes: 60,
  uses_dynamic_hours: true
};

/**
 * Suggested default hours template (9 AM – 6 PM).
 *
 * IMPORTANT: This is a UI-only template used by the dashboard/onboarding to
 * pre-fill the weekly schedule editor. It is NOT used as a fallback for
 * public availability — an unconfigured business must not be bookable.
 *
 * Public booking resolution (`getBusinessHoursForDay`, `generateTimeSlots`)
 * must return null / empty when no rows exist.
 */
const SUGGESTED_DEFAULT_HOURS: { start: number; end: number } = {
  start: 9,
  end: 18,
};

/**
 * Returns a suggested weekly-hours template for the dashboard/onboarding UI.
 *
 * This is intentionally synchronous and DB-free — the template does not
 * depend on any business state and never represents confirmed public
 * availability. Callers must explicitly persist rows to `business_hours`
 * (e.g. via the dashboard Hours page) before those hours become bookable.
 */
export function getSuggestedDefaultHoursTemplate(): { start: number; end: number } {
  return { ...SUGGESTED_DEFAULT_HOURS };
}


// ============================================================================
// BUSINESS SETTINGS & HOURS
// ============================================================================

/**
 * Fetch business settings (cutoff, buffer, slot interval)
 * Returns defaults if not found or if table doesn't exist yet (pre-migration)
 */
export async function getBusinessSettings(businessId: string): Promise<BusinessSettings> {
  try {
    const { data, error } = await supabase
      .from('business_settings')
      .select('same_day_cutoff_minutes, buffer_minutes, slot_interval_minutes, uses_dynamic_hours')
      .eq('business_id', businessId)
      .maybeSingle();

    if (error || !data) {
      console.warn('Using default business settings:', error?.message || 'No settings found');
      return DEFAULT_SETTINGS;
    }

    return {
      same_day_cutoff_minutes: data.same_day_cutoff_minutes ?? DEFAULT_SETTINGS.same_day_cutoff_minutes,
      buffer_minutes: data.buffer_minutes ?? DEFAULT_SETTINGS.buffer_minutes,
      slot_interval_minutes: data.slot_interval_minutes ?? DEFAULT_SETTINGS.slot_interval_minutes,
      uses_dynamic_hours: data.uses_dynamic_hours ?? DEFAULT_SETTINGS.uses_dynamic_hours
    };
  } catch (err) {
    console.warn('Error fetching business settings, using defaults:', err);
    return DEFAULT_SETTINGS;
  }
}

/**
 * Fetch businessId from providerId using DB function
 * Checks staff table first, then falls back to bookings table
 * Returns null if provider not found
 */
export async function getBusinessIdFromProviderId(providerId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .rpc('get_business_id_from_provider', { p_provider_id: providerId });

    if (error) {
      console.warn('Could not fetch businessId from providerId:', error.message);
      return null;
    }

    return data;
  } catch (err) {
    console.warn('Error fetching businessId from providerId:', err);
    return null;
  }
}

/**
 * Fetch confirmed public business hours for a specific day.
 *
 * STRICT SEMANTICS (public availability):
 * - Row exists and `enabled = true` → returns `{ start, end }` (in hours, 0–23).
 * - Row exists and `enabled = false` → returns `null` (business closed that day).
 * - No row exists → returns `null` (business has not yet confirmed hours for
 *   this day; the day is NOT publicly bookable).
 * - DB error → returns `null` (fail closed — never show slots on unknown state).
 *
 * DO NOT use this function to render suggested defaults in the dashboard
 * editor — use `getSuggestedDefaultHoursTemplate()` instead. Defaults must
 * never be presented as confirmed public availability.
 *
 * INVARIANT: With provider_id in schema, defaults to provider_id = NULL
 * (default provider v1). Future: filter by provider_id for per-provider schedules.
 */
export async function getBusinessHoursForDay(
  businessId: string,
  date: Date,
  providerId?: string
): Promise<{ start: number; end: number } | null> {
  try {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayOfWeek = dayNames[date.getDay()];

    let query = supabase
      .from('business_hours')
      .select('start_time, end_time, enabled')
      .eq('business_id', businessId)
      .eq('day_of_week', dayOfWeek);

    // Filter by provider_id if provided; otherwise defaults to NULL (v1 default provider)
    if (providerId) {
      query = query.eq('provider_id', providerId);
    } else {
      query = query.is('provider_id', null);
    }

    // NOTE: We use .limit(1) then take the first row rather than .maybeSingle()
    // because the DB previously allowed duplicate rows for provider_id IS NULL
    // (UNIQUE constraint does not prevent NULL duplicates in Postgres).
    // Migration 20260416_fix_business_hours_null_uniqueness.sql deduplicates
    // existing rows and adds a partial unique index to prevent future duplicates.
    // .limit(1) is kept here as a defensive guard in case migration hasn't run.
    const { data: rows, error } = await query.limit(1);

    if (error) {
      console.error(
        `[availabilityCore] Failed to query business_hours for ${dayOfWeek}`,
        { businessId, providerId: providerId || null, error: error.message, code: error.code }
      );
      // Fail closed — never show slots when hours can't be confirmed
      return null;
    }

    const data = rows?.[0] ?? null;

    if (!data) {
      // STRICT: no configured row means the business has not yet confirmed
      // hours for this day. Do NOT fall back to default hours — a brand-new
      // business must not be publicly bookable until it saves its schedule.
      console.warn(
        `[availabilityCore] No business hours configured for ${dayOfWeek} — day is unavailable`,
        { businessId, providerId: providerId || null }
      );
      return null;
    }

    if (!data.enabled) {
      return null; // Business explicitly closed on this day
    }

    // Parse "HH:MM" or "HH:MM:SS" format
    const start = parseInt(data.start_time.split(':')[0], 10);
    const end = parseInt(data.end_time.split(':')[0], 10);

    return { start, end };
  } catch (err) {
    console.error(`Unexpected error fetching business hours (business_id: ${businessId}, provider_id: ${providerId || 'NULL'}):`, err);
    return null; // Fail closed — don't show slots on unexpected errors
  }
}

/**
 * Fetch business timezone from business_profiles
 * Returns undefined if not set (callers fall back to server time)
 */
export async function getBusinessTimezone(businessId: string): Promise<string | undefined> {
  try {
    const { data, error } = await supabase
      .from('business_profiles')
      .select('timezone')
      .eq('id', businessId)
      .maybeSingle();

    if (error || !data) return undefined;
    return data.timezone || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Check if date is a blackout date for the business
 * Returns true if date is blocked
 */
export async function isBlackoutDate(
  businessId: string,
  date: Date
): Promise<boolean> {
  try {
    const dateStr = date.toISOString().split('T')[0];

    const { data, error } = await supabase
      .rpc('is_blackout_date', {
        p_business_id: businessId,
        p_date: dateStr,
      });

    if (error) {
      console.warn('Error checking blackout date:', error);
      return true; // Fail safe — treat as blocked if DB query fails
    }

    return data === true;
  } catch (err) {
    console.warn('Error checking blackout date:', err);
    return true; // Fail safe — treat as blocked if DB query fails
  }
}

// ============================================================================
// DATE OVERRIDE RESOLUTION
// ============================================================================

export interface DateOverrideResult {
  mode: 'closed' | 'custom_hours';
  timeRanges?: { start: number; startMinute: number; end: number; endMinute: number }[];
}

/**
 * Check for a date-specific override (closed or custom hours).
 *
 * Uses a SECURITY DEFINER RPC (get_date_override) so anonymous booking users
 * can read overrides without direct table access. The RPC returns only the
 * override mode and time ranges — no business PII is exposed.
 *
 * Resolution:
 * - 'closed' → returns { mode: 'closed' }
 * - 'custom_hours' → returns { mode: 'custom_hours', timeRanges }
 * - No override → returns null (caller falls through to weekly hours)
 *
 * Fail-open on RPC error: falls through to weekly hours rather than blocking
 * the day entirely (same graceful-degradation pattern as availability.ts).
 */
export async function getDateOverride(
  businessId: string,
  date: Date,
  providerId?: string
): Promise<DateOverrideResult | null> {
  try {
    const dateStr = date.toISOString().split('T')[0];

    const { data, error } = await supabase.rpc('get_date_override', {
      p_business_id: businessId,
      p_date: dateStr,
      p_provider_id: providerId || null,
    });

    if (error) {
      console.warn('[availabilityCore] Error fetching date override via RPC, falling through to weekly hours:', error);
      return null; // Graceful degradation
    }

    if (!data) return null;

    const result = typeof data === 'string' ? JSON.parse(data) : data;

    if (result.mode === 'closed') {
      return { mode: 'closed' };
    }

    if (result.mode === 'custom_hours') {
      const timeRanges = (result.time_ranges || []).map((r: { start: string; end: string }) => {
        const [sh, sm] = r.start.split(':').map(Number);
        const [eh, em] = r.end.split(':').map(Number);
        return { start: sh, startMinute: sm || 0, end: eh, endMinute: em || 0 };
      });

      return { mode: 'custom_hours', timeRanges };
    }

    return null;
  } catch (err) {
    console.warn('[availabilityCore] Unexpected error in getDateOverride:', err);
    return null; // Graceful degradation
  }
}

// ============================================================================
// TIME SLOT GENERATION
// ============================================================================

/**
 * Apply same-day cutoff filtering to time slots
 * Canonical clock = business local time (pre-MVP implemented using server time)
 *
 * INVARIANTS:
 * - Only applies to "today" in business's local calendar day
 * - Cutoff measured from slot START time in business local time
 * - Rule: slot_start >= business_now + cutoffMinutes
 */
export function applySameDayCutoff(
  slots: string[],
  date: Date,
  cutoffMinutes: number,
  businessTimezone?: string
): string[] {
  const businessNow = getBusinessLocalNow(businessTimezone);

  detectTimezoneMismatch(date, 'applySameDayCutoff');

  // Determine if date matches "today" in business's local calendar day
  const isToday = getDateStringInTimezone(new Date(), businessTimezone) ===
                  getDateStringInTimezone(date, businessTimezone);

  // Future dates: no cutoff filtering
  if (!isToday) {
    return slots;
  }

  // Calculate cutoff time in business local time
  const cutoffMillis = cutoffMinutes * 60 * 1000;
  const cutoffTime = new Date(businessNow.getTime() + cutoffMillis);

  // Filter based on slot START time in business local time
  return slots.filter(slot => {
    const match = slot.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) return false;

    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[3].toUpperCase();

    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;

    // Construct slot time in business local time
    const slotTime = new Date(
      businessNow.getFullYear(),
      businessNow.getMonth(),
      businessNow.getDate(),
      hours,
      minutes,
      0,
      0
    );

    return slotTime >= cutoffTime;
  });
}

/**
 * Generate time slots for a given date based on business hours and settings
 *
 * PRECEDENCE (highest to lowest):
 * 1. Closed date override → empty (zero availability)
 * 2. Custom hours date override → use override time ranges
 * 3. Blackout date → empty (zero availability)
 * 4. Weekly hours → use day-of-week schedule
 *
 * INVARIANTS:
 * - Returns empty array if date override is 'closed'
 * - Returns slots from override ranges if 'custom_hours'
 * - Returns empty array if blackout date
 * - Filters out past times if today
 * - Applies same-day cutoff window (default 120 minutes)
 * - Respects business hours from database
 * - Uses configurable slot duration and buffer
 * - Returns empty array if business is closed
 */
export async function generateTimeSlots(
  date: Date,
  businessId: string,
  settings?: BusinessSettings,
  businessTimezone?: string,
  providerId?: string
): Promise<string[]> {
  // Check 1: Date-specific override (takes highest precedence)
  const dateOverride = await getDateOverride(businessId, date, providerId);
  if (dateOverride) {
    if (dateOverride.mode === 'closed') {
      console.warn('[availabilityCore] Date override: closed', {
        businessId, date: date.toISOString().split('T')[0]
      });
      return [];
    }

    if (dateOverride.mode === 'custom_hours' && dateOverride.timeRanges) {
      // Generate slots from each override time range
      const config = settings || await getBusinessSettings(businessId);
      return generateSlotsFromRanges(
        date, dateOverride.timeRanges, config, businessTimezone
      );
    }
  }

  // Check 2: Blackout date (legacy table — still respected)
  const isBlackout = await isBlackoutDate(businessId, date);
  if (isBlackout) {
    console.warn(`Date is blacked out (business_id: ${businessId})`);
    return [];
  }

  // Fetch settings if not provided
  const config = settings || await getBusinessSettings(businessId);

  // Check 3: Weekly hours for this specific day
  const hours = await getBusinessHoursForDay(businessId, date, providerId);
  if (!hours) {
    console.warn('[availabilityCore] generateTimeSlots: no confirmed hours — returning empty', {
      businessId, date: date.toISOString().split('T')[0], providerId: providerId || null
    });
    return [];
  }

  return generateSlotsFromRanges(
    date, [hours], config, businessTimezone
  );
}

/**
 * Generate time slots from one or more { start, end } hour ranges.
 * Shared by weekly-hours and custom-hours-override paths to avoid
 * duplicating slot-generation, past-time filtering, and cutoff logic.
 */
function generateSlotsFromRanges(
  date: Date,
  ranges: { start: number; startMinute?: number; end: number; endMinute?: number }[],
  config: BusinessSettings,
  businessTimezone?: string
): string[] {
  const businessNow = getBusinessLocalNow(businessTimezone);
  const isToday = getDateStringInTimezone(new Date(), businessTimezone) ===
                  getDateStringInTimezone(date, businessTimezone);
  const currentHour = businessNow.getHours();
  const currentMinute = businessNow.getMinutes();

  const allSlots: string[] = [];

  for (const range of ranges) {
    let currentTime = new Date(date);
    currentTime.setHours(range.start, range.startMinute || 0, 0, 0);
    const endTime = new Date(date);
    endTime.setHours(range.end, range.endMinute || 0, 0, 0);

    while (currentTime < endTime) {
      const slotEndTime = new Date(currentTime.getTime() + config.slot_interval_minutes * 60000);
      if (slotEndTime > endTime) break;

      // Skip past time slots if this is today
      const slotHour = currentTime.getHours();
      const slotMinute = currentTime.getMinutes();
      if (isToday && (slotHour < currentHour || (slotHour === currentHour && slotMinute <= currentMinute))) {
        currentTime = new Date(slotEndTime.getTime() + config.buffer_minutes * 60000);
        continue;
      }

      allSlots.push(currentTime.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true
      }));

      currentTime = new Date(slotEndTime.getTime() + config.buffer_minutes * 60000);
    }
  }

  // Apply same-day cutoff filtering (e.g., remove slots within 120 minutes)
  return applySameDayCutoff(allSlots, date, config.same_day_cutoff_minutes, businessTimezone);
}

// ============================================================================
// BOOKED SLOTS QUERIES
// ============================================================================

/**
 * Convert database booking_time (HH:MM:SS or HH:MM) to display format (HH:MM AM/PM)
 */
export function convertDbTimeToDisplay(dbTime: string): string {
  if (!dbTime) return '';
  const [hours, minutes] = dbTime.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${String(displayHour).padStart(2, '0')}:${minutes} ${ampm}`;
}

/**
 * Calculate which time slots are occupied by a booking
 * Returns array of slot times that should be marked unavailable
 *
 * IMPORTANT: Uses slotInterval to determine how many slots to block
 */
export function calculateOccupiedSlots(
  booking: BookedSlot,
  slotInterval: number = 30
): string[] {
  const slots: string[] = [];
  const [hours, minutes] = booking.booking_time.split(':').map(Number);

  // Calculate how many slots this booking occupies
  const slotsCount = Math.ceil(booking.duration / slotInterval);

  for (let i = 0; i < slotsCount; i++) {
    const slotMinutes = minutes + (i * slotInterval);
    const slotHours = hours + Math.floor(slotMinutes / 60);
    const finalMinutes = slotMinutes % 60;

    const period = slotHours >= 12 ? 'PM' : 'AM';
    const displayHours = slotHours === 0 ? 12 : slotHours > 12 ? slotHours - 12 : slotHours;
    const displayMinutes = finalMinutes.toString().padStart(2, '0');

    slots.push(`${String(displayHours).padStart(2, '0')}:${displayMinutes} ${period}`);
  }

  return slots;
}

/**
 * Fetch booked slots for a specific date and provider/business
 *
 * INVARIANTS:
 * - Filters by provider_id if provided (rescheduling)
 * - Falls back to business_id if no provider (initial booking)
 * - Only includes confirmed/pending bookings
 * - Calculates occupied slots based on duration
 */
export async function fetchBookedSlots(params: {
  businessId?: string;
  providerId?: string;
  date: Date;
  slotInterval?: number;
}): Promise<string[]> {
  const { businessId, providerId, date, slotInterval = 30 } = params;

  if (!businessId && !providerId) {
    console.warn('fetchBookedSlots: no businessId or providerId provided');
    return [];
  }

  const dateStr = date.toISOString().split('T')[0];

  let query = supabase
    .from('bookings')
    .select('booking_time, duration')
    .eq('booking_date', dateStr)
    .in('status', ['confirmed', 'pending']);

  // PRIORITY: provider_id > business_id
  if (providerId) {
    query = query.eq('provider_id', providerId);
  } else if (businessId) {
    query = query.eq('business_id', businessId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching booked slots:', error);
    return [];
  }

  if (!data || data.length === 0) {
    return [];
  }

  // Convert all bookings to occupied time slots
  const occupiedSlots = data.flatMap((booking: any) =>
    calculateOccupiedSlots(
      {
        booking_time: booking.booking_time,
        duration: booking.duration || 60
      },
      slotInterval
    )
  );

  // Remove duplicates
  return [...new Set(occupiedSlots)];
}

// ============================================================================
// CUTOFF LOGIC
// ============================================================================

/**
 * PRODUCT MODEL (Non-negotiable):
 * Same-day cutoff is evaluated in the business's local calendar day and local clock time.
 * If user timezone ≠ business timezone, cutoff still follows business time.
 *
 * PRE-MVP ASSUMPTION:
 * - Business timezone is canonical for all cutoff and availability logic.
 * - Pre-MVP: Business timezone = Server timezone (same location/region).
 * - All times interpreted as business local time (server's clock).
 * - Future: Add `timezone` column to business_profiles for multi-timezone support.
 */

/**
 * Detect potential timezone offset mismatch (for debugging only)
 * Logs potential mismatches only; differing offsets do not change behavior, which is always governed by business local time.
 */
function detectTimezoneMismatch(inputDate: Date, context: string): void {
  const now = new Date();
  const timezoneOffset = now.getTimezoneOffset();
  const inputOffset = inputDate.getTimezoneOffset();

  if (timezoneOffset !== inputOffset) {
    console.warn(
      `[${context}] Input date has different timezone offset (may be intentional):`,
      {
        serverOffset: timezoneOffset,
        inputOffset,
        serverTz: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    );
  }
}

/**
 * Check if a booking is within the cutoff window
 * Canonical clock = business local time (pre-MVP implemented using server time)
 *
 * Returns true if too close to reschedule (within cutoff window)
 * Rule: business_now >= booking_start - cutoffMinutes
 */
export function isWithinCutoff(
  bookingDate: string,    // "YYYY-MM-DD"
  bookingTime: string,    // "HH:MM:SS" or "HH:MM"
  cutoffMinutes: number
): boolean {
  try {
    const [year, month, day] = bookingDate.split('-').map(Number);
    const [hours, minutes] = bookingTime.split(':').map(Number);

    // Parse as business local time
    const bookingStart = new Date(year, month - 1, day, hours, minutes, 0, 0);

    const businessNow = new Date(); // Canonical clock = business local time (pre-MVP implemented using server time)
    const cutoffTime = new Date(bookingStart.getTime() - (cutoffMinutes * 60 * 1000));

    return businessNow >= cutoffTime;
  } catch (err) {
    console.error('Error calculating cutoff:', err);
    return false; // Fail open (allow reschedule on error)
  }
}

/**
 * Check if a booking is in the past
 */
export function isBookingInPast(
  bookingDate: string,
  bookingTime: string
): boolean {
  try {
    const [hours, minutes] = bookingTime.split(':').map(Number);
    const bookingStart = new Date(bookingDate);
    bookingStart.setHours(hours, minutes, 0, 0);

    return bookingStart < new Date();
  } catch (err) {
    console.error('Error checking if booking is in past:', err);
    return false;
  }
}

/**
 * SINGLE SOURCE OF TRUTH for cutoff state
 *
 * Used by BOTH normal and manage booking confirmation modes.
 * Returns cutoff configuration and whether booking is within cutoff window.
 *
 * INVARIANTS:
 * - Never throws (catches all errors)
 * - Always returns valid state (fallback to safe defaults)
 * - Logs errors clearly
 * - Uses MINUTES not hours
 */
export async function getCutoffState(params: {
  businessId: string;
  bookingDate: string;
  bookingTime: string;
}): Promise<{
  withinCutoff: boolean;
  cutoffHours: number;  // Kept for backward compat, value is minutes/60
}> {
  const { businessId, bookingDate, bookingTime } = params;

  try {
    // Load business settings (includes cutoff config in MINUTES)
    const settings = await getBusinessSettings(businessId);
    const cutoffMinutes = settings.same_day_cutoff_minutes;

    // Calculate if within cutoff window
    const withinCutoff = isWithinCutoff(bookingDate, bookingTime, cutoffMinutes);

    return {
      withinCutoff,
      cutoffHours: Math.floor(cutoffMinutes / 60)  // Convert to hours for display
    };
  } catch (error) {
    console.error('Error calculating cutoff state, using safe defaults:', error);

    // SAFE FALLBACK: Assume NOT within cutoff (allow reschedule)
    // This is safer than blocking users due to an error
    return {
      withinCutoff: false,
      cutoffHours: 2  // Default 2-hour cutoff
    };
  }
}
